import React from "react";

import RegisterSubmitButton from "./RegisterSubmitButton";

const inputStyle = {
  width: "100%",
  padding: "12px",
  marginBottom: "12px",
  borderRadius: "8px",
  border: "1px solid #ccc",
  fontSize: "15px",
};

const buttonStyle = {
  width: "100%",
  background: "#1fa4c5",
  border: "none",
  padding: "12px",
  borderRadius: "8px",
  color: "#fff",
  fontSize: "16px",
  fontWeight: "bold",
  cursor: "pointer",
  marginBottom: "14px",
};

const dropdownBoxStyle = {
  ...inputStyle,
  marginBottom: "8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",
  background: "#fff",
};

const dropdownPanelStyle = {
  position: "relative",
  marginBottom: "12px",
};

const dropdownListStyle = {
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  maxHeight: "200px",
  overflowY: "auto",
  background: "#fff",
  border: "1px solid #ccc",
  borderTop: "none",
  borderRadius: "0 0 8px 8px",
  padding: "4px 0",
  zIndex: 1000,
  textAlign: "left",
  boxShadow: "0 6px 14px rgba(0,0,0,0.12)",
};

const fieldLabelRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  marginBottom: "6px",
  textAlign: "left",
};

const fieldLabelStyle = {
  color: "#35566d",
  fontSize: "14px",
  fontWeight: 600,
  lineHeight: 1.35,
};

const fieldHelpWrapStyle = {
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  flexShrink: 0,
};

const fieldHelpButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  padding: 0,
  border: "1px solid #c9d6e2",
  borderRadius: "999px",
  background: "#f4f8fb",
  color: "#2d4759",
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: 1,
  cursor: "pointer",
};

const fieldHelpPopoverStyle = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  width: "min(280px, calc(100vw - 96px))",
  padding: "10px 12px",
  borderRadius: "10px",
  background: "rgba(24, 33, 45, 0.96)",
  color: "#fff",
  fontSize: "12px",
  lineHeight: 1.45,
  boxShadow: "0 6px 16px rgba(0,0,0,0.18)",
  zIndex: 1100,
  textAlign: "left",
};

function renderFieldHelpLabel(register, fieldId, label, helpText, htmlFor) {
  const isOpen = register.openHelpField === fieldId;
  const labelContent = htmlFor ? (
    <label htmlFor={htmlFor} style={fieldLabelStyle}>
      {label}
    </label>
  ) : (
    <span style={fieldLabelStyle}>{label}</span>
  );

  return (
    <div style={fieldLabelRowStyle}>
      {labelContent}
      <div style={fieldHelpWrapStyle} data-register-help-root="true">
        <button
          type="button"
          style={fieldHelpButtonStyle}
          aria-label={register.t("register_field_help_aria", {
            defaultValue: "{{label}} info",
            label,
          })}
          aria-expanded={isOpen}
          aria-controls={`register-help-${fieldId}`}
          onClick={() =>
            register.setOpenHelpField((prev) => (prev === fieldId ? "" : fieldId))
          }
        >
          i
        </button>
        {isOpen ? (
          <div
            id={`register-help-${fieldId}`}
            role="tooltip"
            style={fieldHelpPopoverStyle}
          >
            {helpText}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function renderMultiSelectDropdown({
  register,
  fieldId,
  label,
  helpText,
  isOpen,
  setIsOpen,
  selectedValues,
  toggleValue,
  options,
  displayLabel,
}) {
  return (
    <div style={dropdownPanelStyle}>
      {renderFieldHelpLabel(register, fieldId, label, helpText)}
      <div style={dropdownBoxStyle} onClick={() => setIsOpen((open) => !open)}>
        <span
          style={{
            color: selectedValues.length ? "#000" : "#999",
            fontSize: "14px",
          }}
        >
          {displayLabel}
        </span>
        <span>{"\u25BC"}</span>
      </div>

      {isOpen ? (
        <div style={dropdownListStyle}>
          {[...options]
            .sort((a, b) => register.t(a).localeCompare(register.t(b)))
            .map((option) => (
              <label
                key={option}
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "4px",
                  fontSize: "14px",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedValues.includes(option)}
                  onChange={() => toggleValue(option)}
                  style={{ marginRight: "6px" }}
                />
                {register.t(option)}
              </label>
            ))}
        </div>
      ) : null}
    </div>
  );
}

export default function RegisterProfileSetup({ register }) {
  const isPersonalAccount = register.accountType === "personal";

  return (
    <>
      <h2 style={{ marginBottom: "20px", color: "#1fa4c5" }}>
        {register.t("register_optional_profile_title", {
          defaultValue: "Optional profile details",
        })}
      </h2>

      <p style={{ margin: "0 0 18px", color: "#5f7281", fontSize: "14px" }}>
        {register.t("register_optional_profile_intro", {
          defaultValue:
            "Add more about yourself now, or skip this and complete your account.",
        })}
      </p>

      <textarea
        placeholder={register.t("bio_optional")}
        value={register.bio}
        onChange={(e) => register.setBio(e.target.value)}
        style={{ ...inputStyle, height: "70px", resize: "none" }}
      />

      {isPersonalAccount ? (
        <>
          <div style={{ marginBottom: "12px" }}>
            {renderFieldHelpLabel(
              register,
              "waterType",
              register.t("preferred_fishing_water_type", {
                defaultValue: "Preferred fishing water type",
              }),
              register.t("register_help_water_type", {
                defaultValue:
                  "Select the kind of waters you usually fish in. Choose freshwater for lakes and rivers, salt water (marine) for seas and oceans, or all if you fish in both.",
              }),
              "register-water-type"
            )}
            <select
              id="register-water-type"
              value={register.waterType}
              onChange={(e) => register.setWaterType(e.target.value)}
              style={inputStyle}
            >
              <option value="">
                {register.t("preferred_fishing_water_type", {
                  defaultValue: "Preferred fishing water type",
                })}
              </option>
              <option value="both">
                {register.t("both", { defaultValue: "Both" })}
              </option>
              <option value="freshwater">{register.t("freshwater")}</option>
              <option value="saltwater">
                {register.t("preferred_fishing_water_type_saltwater_marine", {
                  defaultValue: "Salt water (marine)",
                })}
              </option>
            </select>
          </div>

          <div style={{ marginBottom: "12px" }}>
            {renderFieldHelpLabel(
              register,
              "fishingStyle",
              register.t("catch_way_of_fishing", {
                defaultValue: "Way of Fishing",
              }),
              register.t("register_help_fishing_style", {
                defaultValue:
                  "Choose the way of fishing you use most often, such as shore or boat fishing. This helps personalize your profile and recommendations.",
              }),
              "register-fishing-style"
            )}
            <select
              id="register-fishing-style"
              value={register.fishingStyle}
              onChange={(e) => register.setFishingStyle(e.target.value)}
              style={inputStyle}
            >
              <option value="">
                {register.t("catch_way_of_fishing", {
                  defaultValue: "Way of Fishing",
                })}
              </option>
              <option value="shore">{register.t("shore")}</option>
              <option value="boat">{register.t("boat")}</option>
              <option value="both">{register.t("both_any")}</option>
            </select>
          </div>

          <div style={{ marginBottom: "12px" }}>
            {renderFieldHelpLabel(
              register,
              "rangeType",
              register.t("fishing_range", { defaultValue: "Fishing range" }),
              register.t("register_help_range_type", {
                defaultValue:
                  "Select the typical range of your fishing activity. For example, local for nearby spots or wider ranges if you travel more often for fishing.",
              }),
              "register-range-type"
            )}
            <select
              id="register-range-type"
              value={register.rangeType}
              onChange={(e) => register.setRangeType(e.target.value)}
              style={inputStyle}
            >
              <option value="">{register.t("fishing_range")}</option>
              <option value="local">{register.t("local")}</option>
              <option value="international">
                {register.t("international")}
              </option>
              <option value="both">{register.t("both_any")}</option>
            </select>
          </div>

          <div style={{ marginBottom: "12px" }}>
            {renderFieldHelpLabel(
              register,
              "experience",
              register.t("experience", { defaultValue: "Experience" }),
              register.t("register_help_experience", {
                defaultValue:
                  "Share your fishing experience level so the app can better match content, tips, and features to your background. This field is optional.",
              }),
              "register-experience"
            )}
            <select
              id="register-experience"
              value={register.experience}
              onChange={(e) => register.setExperience(e.target.value)}
              style={inputStyle}
            >
              <option value="">
                {register.t("select_experience_optional", {
                  defaultValue: "Experience (optional)",
                })}
              </option>
              <option value="beginner">{register.t("beginner")}</option>
              <option value="intermediate">
                {register.t("intermediate")}
              </option>
              <option value="expert">{register.t("expert")}</option>
              <option value="pro">{register.t("pro")}</option>
            </select>
          </div>

          {renderMultiSelectDropdown({
            register,
            fieldId: "methods",
            label: register.t("catch_type_of_fishing", {
              defaultValue: "Type of Fishing",
            }),
            helpText: register.t("register_help_methods", {
              defaultValue:
                "Choose the type of fishing you enjoy or use most often. This helps shape your profile and improve relevant recommendations. This field is optional.",
            }),
            isOpen: register.showMethodsDropdown,
            setIsOpen: register.setShowMethodsDropdown,
            selectedValues: register.selectedMethods,
            toggleValue: register.toggleMethod,
            options: register.fishingMethods,
            displayLabel: register.methodsLabel,
          })}

          {renderMultiSelectDropdown({
            register,
            fieldId: "favoriteSpecies",
            label: register.t("favorite_species", {
              defaultValue: "Favorite species",
            }),
            helpText: register.t("register_help_species", {
              defaultValue:
                "Pick the fish species you are most interested in catching or learning about. This helps personalize your experience. This field is optional.",
            }),
            isOpen: register.showSpeciesDropdown,
            setIsOpen: register.setShowSpeciesDropdown,
            selectedValues: register.selectedSpecies,
            toggleValue: register.toggleSpecies,
            options: register.speciesOptions,
            displayLabel: register.speciesLabel,
          })}
        </>
      ) : null}

      <div style={{ marginBottom: "12px" }}>
        {renderFieldHelpLabel(
          register,
          "instagram",
          register.t("instagram_url", { defaultValue: "Instagram URL" }),
          register.t("register_help_instagram", {
            defaultValue:
              "Add your Instagram profile link if you want others to discover your fishing content. This field is optional.",
          }),
          "register-instagram"
        )}
        <input
          id="register-instagram"
          type="text"
          placeholder={register.t("instagram_url")}
          value={register.instagram}
          onChange={(e) => register.setInstagram(e.target.value)}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: "12px" }}>
        {renderFieldHelpLabel(
          register,
          "youtube",
          register.t("youtube_url", { defaultValue: "YouTube URL" }),
          register.t("register_help_youtube", {
            defaultValue:
              "Add your YouTube channel or profile link if you share fishing videos or related content. This field is optional.",
          }),
          "register-youtube"
        )}
        <input
          id="register-youtube"
          type="text"
          placeholder={register.t("youtube_url")}
          value={register.youtube}
          onChange={(e) => register.setYoutube(e.target.value)}
          style={inputStyle}
        />
      </div>

      <div style={{ marginBottom: "12px" }}>
        {renderFieldHelpLabel(
          register,
          "facebook",
          register.t("facebook_url", { defaultValue: "Facebook URL" }),
          register.t("register_help_facebook", {
            defaultValue:
              "Add your Facebook profile or page link if you want to share more about your fishing activity. This field is optional.",
          }),
          "register-facebook"
        )}
        <input
          id="register-facebook"
          type="text"
          placeholder={register.t("facebook_url")}
          value={register.facebook}
          onChange={(e) => register.setFacebook(e.target.value)}
          style={inputStyle}
        />
      </div>

      <input
        type="text"
        placeholder={register.t("website")}
        value={register.website}
        onChange={(e) => register.setWebsite(e.target.value)}
        style={inputStyle}
      />

      <RegisterSubmitButton register={register} />

      <button
        onClick={() => register.setStep(2)}
        style={{ ...buttonStyle, background: "#777" }}
      >
        {register.t("back")}
      </button>
    </>
  );
}
